import { GlobalSearchTabPanelProps } from './GlobalSearchTabPanelTypes';
import { fetchUnifiedSearch } from '@/lib/api';
import useInfiniteScroll from '@/hooks/useInfiniteScroll';
import { useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import {
  GameSchema,
  UnifiedSearchResponse,
  WithFormatted,
} from '@/lib/types';
import { getFirstGameByRTP } from '@/lib/game';
import GameCard from '@/components/GameCard/GameCard';
import CircularProgress from '@mui/material/CircularProgress';
import { FormattedMessage } from 'react-intl';
import { ga } from '@/lib/analytics';

const GamesSearchTabPanel = ({
  query,
  onResultNavigate,
  scrollContainerRef,
  onResultsLoaded,
}: GlobalSearchTabPanelProps) => {
  const trimmedQuery = query.trim();
  const hasSufficientQuery = trimmedQuery.length >= 2;

  const {
    data,
    isLoading,
    isFetchingNextPage,
    total_count,
  } = useInfiniteScroll<
    WithFormatted<GameSchema>[],
    Record<string, unknown>,
    UnifiedSearchResponse
  >({
    section: 'Global Search Games',
    fetchFn: ({ offset, limit }) =>
      fetchUnifiedSearch({
        q: trimmedQuery,
        ttype: ['games'],
        offset,
        limit,
      }),
    queryKey: ['global-search', 'games', trimmedQuery],
    scrollContainerRef,
    useWindowScroll: false,
    pageSize: 20,
    enabled: hasSufficientQuery,
    extractData: (response) =>
      response.games?.hits
        ? Object.values(response.games.hits).filter(
            (groupGames) => groupGames && groupGames.length > 0,
          )
        : [],
  });

  const hasResults = useMemo(() => data.length > 0, [data]);

  useEffect(() => {
    if (!hasSufficientQuery || isLoading) return;
    onResultsLoaded?.({
      query: trimmedQuery,
      scope: 'games',
      resultCount: total_count ?? data.length,
    });
  }, [
    data.length,
    hasSufficientQuery,
    isLoading,
    onResultsLoaded,
    total_count,
    trimmedQuery,
  ]);

  return (
    <ScrollableTabContent className="wide-scrollbar">
      <ResultsGrid>
        {data.map((groupGames, index) => {
          const defaultGame =
            getFirstGameByRTP(groupGames) || groupGames[0];
          return (
            <GameCard
              key={defaultGame.id || index}
              games={groupGames}
              defaultGame={defaultGame}
              selected={false}
              section="Global Search Games"
              onDetailsNavigate={() => {
                ga.trackSearchResultClick({
                  result_type: 'game',
                  result_id: defaultGame.studio_game_id,
                  position: index + 1,
                  search_term: trimmedQuery,
                });
                onResultNavigate?.();
              }}
            />
          );
        })}
        {!isLoading && !isFetchingNextPage && !hasResults && (
          <NoResultsText>
            <FormattedMessage
              id="noSearchResults"
              defaultMessage="No search results."
            />
          </NoResultsText>
        )}
        {(isLoading || isFetchingNextPage) && (
          <LoaderContainer>
            <CircularProgress />
          </LoaderContainer>
        )}
      </ResultsGrid>
    </ScrollableTabContent>
  );
};

export default GamesSearchTabPanel;

const ScrollableTabContent = styled('div')({});

const ResultsGrid = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 26,
  width: '100%',
  minHeight: 200,
  paddingBottom: theme.spacing(2),
}));

const LoaderContainer = styled('div')({
  width: '100%',
  padding: '40px',
  display: 'flex',
  justifyContent: 'center',
});

const NoResultsText = styled('div')(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(6),
  textAlign: 'center',
  color: theme.palette.textSecondaryDarkGrey,
}));
